import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { SkyTvButton } from '@mobi/component-library/Areas/Racing/SkyTvIndicator'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { getTicketLayoutType } from '@core/Areas/Account/Pages/Activity/Components/Ticket/helpers'
import { setActiveReplayTicketId } from '@core/Areas/Account/Pages/Activity/Store'
import type { Ticket } from '@core/Areas/Account/Pages/Activity/types'
import { trackRaceReplayVideoLaunched } from '@core/Areas/Racing/Components/RaceReplay/analytics'
import { getReplayInfoFromApi } from '@core/Areas/Racing/Components/RaceReplay/api'
import type { ReplayInfo } from '@core/Areas/Racing/Components/RaceReplay/types'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { keys } from '@core/Services/Analytics/keys'
import { trackKey } from '@core/Services/Analytics/track'
import { logError } from '@core/Utils'

export const RaceReplayButton: React.FC<{
  ticket: Ticket
  isPlaying: boolean
  onPlayClick?: (replayInfo: ReplayInfo) => void
  onCloseClick(): void
}> = ({ ticket, onPlayClick, isPlaying, onCloseClick }) => {
  const dispatch = useDispatch()

  const ticketType = getTicketLayoutType(ticket)
  const firstLeg = ticket.legs[0]
  const canReplay =
    (ticketType === 'single-single' || ticketType === 'same-race-multi') &&
    firstLeg &&
    firstLeg.fixtureId &&
    ticket.status === 'Resolved'

  if (!canReplay) {
    return null
  }

  const onClick = (replayInfo: ReplayInfo) => {
    onPlayClick?.(replayInfo)
    dispatch(setActiveReplayTicketId(ticket.ticketSequence))
    trackKey(keys.raceReplayOpened)
  }

  return (
    <ReplayButtonLocal
      ticket={ticket}
      onReady={info => onClick(info)}
      isPlaying={isPlaying}
      onCloseClick={onCloseClick}
    />
  )
}

// ===============
// Local Component
// ===============

const ReplayButtonLocal: React.FC<{
  ticket: Ticket
  isPlaying: boolean
  onCloseClick(): void
  onReady?: (replayInfo: ReplayInfo) => void
}> = ({ ticket, onReady, isPlaying, onCloseClick }) => {
  const [buttonState, setButtonState] = React.useState<'ready' | 'loading' | 'no_replay'>('ready')

  React.useEffect(() => {
    if (buttonState === 'no_replay') {
      addToast({
        id: buttonState,
        icon: 'error',
        title: 'No replay found',
      })
    }
  }, [buttonState])

  const queryClient = useQueryClient()

  const handleClick = React.useCallback(async () => {
    setButtonState('loading')
    try {
      const { fixtureId, fixtureDate, raceNumber } = ticket.legs[0]
      const data = await queryClient.fetchQuery({
        queryKey: queryKeys.raceReplayRequest(fixtureId, fixtureDate, +raceNumber),
        queryFn: () => getReplayInfoFromApi(fixtureId, fixtureDate, +raceNumber),
        staleTime: Infinity,
      })

      if (data) {
        setButtonState('ready')
        onReady?.(data)
      } else {
        setButtonState('no_replay')
      }

      trackRaceReplayVideoLaunched('resulted-bets', !!data)
    } catch {
      setButtonState('no_replay')
      logError('Could not load race replay')
    }
  }, [ticket, queryClient, onReady, setButtonState])

  if (!ticket.legs[0] || buttonState === 'no_replay') {
    return null
  }

  const isLoading = buttonState === 'loading'

  return (
    <span data-testid='ticket-footer-replay-button'>
      <SkyTvButton
        onClick={isPlaying ? onCloseClick : handleClick}
        channel='Replay'
        isPlaying={isPlaying}
        isLoading={isLoading}
        isLive={false}
      />
    </span>
  )
}
